/* eslint-disable id-length */

import { ChartDataset, TooltipModel } from "chart.js";
import { take } from "lodash";
import { Fragment, useEffect } from "react";

import { useChart } from "~/components/shared/Chart";
import LineChart, { LineChartUnit } from "~/components/ClustersInsights/LineChart";
import { TimePeriod } from "~/components/ClustersInsights/Renderer";
import { Tooltip, TooltipLabel, TooltipValue } from "~/components/ClustersInsights/Tooltip";
import { ChartLegend } from "~/components/ClustersInsights/ChartLegend";
import {
  Card,
  CardSection,
  CardChart,
  CardDelta,
  CardStat,
  CardLabelWithTooltip,
} from "~/components/ClustersInsights/Card";
import SparkLineChart, { dataForSparkline } from "~/components/ClustersInsights/SparkLineChart";
import {
  calculateAverage,
  calculateMin,
  calculateMax,
  formatDuration,
  getTimePeriodString,
  labelHeader,
  Scope,
  getThemeFromDelta,
} from "~/components/ClustersInsights/Chart/utils";
import { useChartData } from "~/components/ClustersInsights/Chart/hooks";
import { InfoTooltip } from "~/components/ClustersInsights/Chart/InfoTooltip";

interface DataPoint {
  x: number;
  y: number;
}

export function QueueWaitTimeSpark({
  datasets: { current, previous },
  p95,
}: {
  datasets: {
    current: DataPoint[];
    previous: DataPoint[];
  };
  p95: number;
}) {
  const currentAvg = calculateAverage(current);
  const previousAvg = calculateAverage(previous);

  const delta = currentAvg - previousAvg;
  const theme = getThemeFromDelta(delta, true);

  return (
    <Card className="relative z-10">
      <CardSection className="items-start justify-between">
        <CardLabelWithTooltip
          label="Queue wait time"
          tooltip={{
            title: `Average time jobs spend waiting to start · ${getTimePeriodString(TimePeriod.Hour)}`,
            description:
              "The average time between a job's creation and when an agent starts the job. Helps identify bottlenecks in your CI worfkflows.",
          }}
        />
        <div className="flex items-center">
          {!!delta && (
            <CardDelta
              formattedValue={formatDuration(Math.abs(delta))}
              value={delta}
              color={theme.color}
            />
          )}
        </div>
      </CardSection>

      <CardSection>
        <CardStat value={formatDuration(currentAvg)} label="Avg" size="lg" layout="stacked" />
        <CardStat value={formatDuration(p95)} label="p95" />
      </CardSection>

      <CardChart>
        <SparkLineChart data={dataForSparkline(previous, current, "y", theme)} />
      </CardChart>
    </Card>
  );
}

export function QueueWaitTimeDetail({
  dataset,
  timePeriod,
  scope,
}: {
  timePeriod: TimePeriod;
  scope: Scope;
  dataset: Array<ChartDataset<"line", DataPoint[]> & { delta: number }>;
}) {
  const { chartRef, updateData } = useChart<"line", DataPoint>();

  const data = useChartData({
    datasets: dataset,
    orderDatasetBy: ({ data }) => calculateAverage(data),
  });

  useEffect(() => {
    updateData(data);
  }, [data, updateData]);

  return (
    <Card className="lg:flex-row">
      <div className="flex min-w-0 grow flex-col gap-3">
        <CardSection className="justify-start gap-x-1">
          <CardLabelWithTooltip
            label="Queue wait time"
            description={`Job delay trends by cluster · ${getTimePeriodString(timePeriod)}`}
            tooltip={{
              title: "Queue Wait Time",
              description:
                "Monitor agent assignment delays across your clusters. Sustained spikes indicate agent availability constraints. Brief spikes that resolve themselves reveal high job-activity periods with matching agent availability.",
            }}
          />
        </CardSection>

        <CardChart>
          <LineChart
            data={data}
            ref={chartRef}
            tooltip={ChartTooltip}
            units={{ y: LineChartUnit.duration }}
          />
        </CardChart>
      </div>

      <ChartLegend
        labelHeader={labelHeader(scope, "Queue")}
        datasets={data.datasets}
        chartRef={chartRef}
        columns={{
          Avg: ({ data, delta }, _index, visible) => {
            const avg = formatDuration(calculateAverage(data));

            const theme = getThemeFromDelta(delta, true);

            return (
              <>
                {avg}
                {!!delta && (
                  <CardDelta
                    className="ml-1 text-[11px]"
                    formattedValue={formatDuration(Math.abs(delta))}
                    value={delta}
                    color={visible ? theme.color : "#c2cace"}
                  />
                )}
              </>
            );
          },
          Min: ({ data }) => formatDuration(calculateMin(data)),
          Max: ({ data }) => formatDuration(calculateMax(data)),
        }}
      />
    </Card>
  );
}

const ChartTooltip = ({ tooltip }: { tooltip: TooltipModel<"line"> }) => {
  const visibleDataPoints = take(tooltip.dataPoints, 8);
  const hiddenDataPoints = tooltip.dataPoints.length - 8;

  return (
    <Tooltip tooltip={tooltip}>
      {visibleDataPoints.map(({ dataset, parsed }) => (
        <Fragment key={dataset.label}>
          <TooltipLabel color={dataset.borderColor as string}>{dataset.label}</TooltipLabel>
          <TooltipValue>{formatDuration(parsed.y)}</TooltipValue>
        </Fragment>
      ))}

      {hiddenDataPoints > 0 && (
        <span className="text-charcoal-300 col-span-2 text-[11px] font-medium">
          ({hiddenDataPoints} not shown)
        </span>
      )}
    </Tooltip>
  );
};
